/**
 * GuardedRoute - Per-route guard wrapper component
 *
 * Executes route-level guards (role, vendor, provider) AFTER authentication.
 * Authentication is handled at the layout level (RequireAuth + AppShell).
 * This component handles the remaining guards for individual routes.
 *
 * Mirrors legacy app's per-route canActivate: [RoleGuard, VendorRoleGuard, ProviderViewGuard]
 */

import React, { Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { Router } from '@uhg-abyss/web/ui/Router';
import { GuardType, RouteConfig } from '../types';
import { useRouteGuards } from '../hooks/useRouteGuards';
import { RouteLoading } from '../components/RouteLoading';
import { ChunkErrorBoundary } from '../components/ChunkErrorBoundary';

interface GuardedRouteProps {
  /** Route configuration with guards and metadata */
  config: RouteConfig;
  /** The component to render (lazy-loaded) */
  children: React.ReactNode;
}

const isExternalUser = () => {
  try {
    const currentUser = JSON.parse(sessionStorage.getItem('currentUser') || '[]');
    const user = Array.isArray(currentUser) ? currentUser[0] : currentUser;
    return user?.UserPersonas?.[0]?.UserType === 'External';
  } catch {
    return false;
  }
};

/**
 * Wraps a route with guard checks + metadata (title).
 * Use inside a protected layout where auth is already verified.
 */
export const GuardedRoute: React.FC<GuardedRouteProps> = ({ config, children }) => {
  const isExternalOrSimulation =
    isExternalUser() || sessionStorage.getItem('simulationMode') === 'true';

  // Skip 'auth' since it's already handled by RequireAuth at layout level
  const routeGuards = (config.guards || []).filter((g) => g !== 'auth') as GuardType[];
  const guardResult = useRouteGuards(routeGuards, config);

  if (isExternalOrSimulation && config.data?.externalAccess === false) {
    return <Navigate to="/myinsights" replace />;
  }

  if (!guardResult.allowed) {
    return <Navigate to={guardResult.redirect || '/AccessDenied'} replace />;
  }

  return (
    <React.Fragment>
      {config.data?.title && <Router.MetaTags title={config.data.title} />}
      <ChunkErrorBoundary>
        <Suspense fallback={<RouteLoading />}>{children}</Suspense>
      </ChunkErrorBoundary>
    </React.Fragment>
  );
};

export default GuardedRoute;
